import type {
  ProposalNegotiationParty,
  ProposalResolutionState,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import type { ResolutionIncomingAuthor } from "work/proposals/redlining/field-redlines/resolution-incoming-author";

/**
 * A resolution target is an in-field mine/theirs conflict the viewer resolves — a **Counterparty**
 * redline the other party published (`incomingParty` differs from the viewer). Resolution
 * (Accept/Reject/Undo) has meaning only against an *incoming opposing-party* value.
 *
 * Reviewer / own-party changes (`incomingParty === viewingParty`, incl. `origin === "Reviewer"`) are
 * deliberately NOT resolvable: they are the viewer's own party's outgoing work, already present in the
 * projection, and rendering them as redline conflated them with counterparty changes and broke undo
 * (an own-party change has no accept/reject-from-opposing-party semantics). They surface through the
 * field change history instead, and edits on top of them stay ordinary undoable blue-lines. A missing
 * `origin` is a historical Counterparty target, correctly classified by the party check alone.
 */
export function isProposalResolutionTargetResolvable(
  state: ProposalResolutionState,
  viewingParty: ProposalNegotiationParty,
): boolean {
  return state.incomingParty !== viewingParty;
}

/**
 * The "theirs" author descriptor surfaced onto the redline so the resolution UI can
 * label who proposed the change.
 *
 * Always `Counterparty`: reviewer (intra-party) resolution targets were retired server-side
 * (ROADMAP.md item 2), and this function's callers already restrict `state` to
 * {@link isProposalResolutionTargetResolvable} states, which a same-party reviewer target could
 * never be even before the retirement (`incomingParty === viewingParty`) — so there was never a
 * live path here to observe `origin === "Reviewer"`.
 */
export function proposalResolutionTargetIncomingAuthor(
  state: ProposalResolutionState,
): ResolutionIncomingAuthor {
  return {
    origin: "Counterparty",
    role: state.incomingActorRole ?? null,
    userId: state.incomingActorUserId ?? null,
    legalEntityId: state.incomingActorLegalEntityId ?? null,
  };
}
